import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Download,
  Maximize2,
  Minimize2,
  Loader2,
  Check,
  ChevronsUpDown,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "../../components/ui/toggle-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import { cn } from "../../lib/utils";
import { useSidebar } from "../../context/SidebarContext";
import {
  ConsolidatedFilters,
  getFYLabel,
  getPortfolioFYOptions,
} from "../../lib/consolidated-cashflow";
import { normalizePortfolioCashflowCustomersResponse } from "../../lib/portfolio-stats";
import apiService from "../../services/apiService";
import * as XLSX from "xlsx";
import { CashflowGlossarySheet } from "./CashflowGlossarySheet";
import { ConsolidatedCashflowKpiStrip } from "./ConsolidatedCashflowKpiStrip";
import CashflowForecastTable, {
  buildPortfolioCashflowSheetData,
  normalizeCashflowApiResponse,
} from "./CashflowForecastTable";
import { toast } from "react-toastify";

interface MultiPickProps {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (v: string[]) => void;
}

const MultiPick = ({ label, options, selected, onChange }: MultiPickProps) => {
  const [open, setOpen] = useState(false);
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5">
          {label}
          {selected.length > 0 && (
            <Badge variant="secondary" className="h-4 px-1 text-[10px]">
              {selected.length}
            </Badge>
          )}
          <ChevronsUpDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => toggle(opt.value)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected.includes(opt.value) ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
            {selected.length > 0 && (
              <div className="border-t p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-center text-xs h-7"
                  onClick={() => onChange([])}
                >
                  Clear all
                </Button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface ConsolidatedCashflowTabProps {
  /** Custom table slot; defaults to consolidated API cashflow forecast table. */
  tableContent?: ReactNode;
}

export const ConsolidatedCashflowTab = ({
  tableContent,
}: ConsolidatedCashflowTabProps) => {
  const { sidebarWidth } = useSidebar();

  const currentFY = getFYLabel(new Date());
  const [expanded, setExpanded] = useState(false);
  const [filters, setFilters] = useState<ConsolidatedFilters>({
    granularity: "monthly",
    fy: currentFY,
    customerIds: [],
    lenders: [],
  });

  const [customerOptions, setCustomerOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [tableRowCount, setTableRowCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const response = await apiService.get<unknown>(
          "/rental-schedule/get-portfolio-cashflow-customers"
        );
        if (cancelled) return;
        const customers = normalizePortfolioCashflowCustomersResponse(response);
        setCustomerOptions(
          customers.map((c) => ({
            value: c.customerId,
            label: c.customerName,
          }))
        );
      } catch (error) {
        console.error("Failed to load portfolio cashflow customers:", error);
        if (!cancelled) setCustomerOptions([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const fyOptions = useMemo(() => getPortfolioFYOptions(), []);

  const kpiFilters = useMemo(
    () => ({
      customerIds: filters.customerIds,
      lenders: filters.lenders,
    }),
    [filters.customerIds, filters.lenders]
  );

  const resolvedTableContent =
    tableContent ?? (
      <CashflowForecastTable
        showToolbar={false}
        className="h-full"
        variant="portfolio"
        consolidatedFilters={filters}
        onRowCountChange={setTableRowCount}
      />
    );

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await apiService.get<unknown>(
        "/rental-schedule/get-consolidated-rental-schedule-cashflow"
      );
      const { rows, totals } = normalizeCashflowApiResponse(response);
      if (!rows.length) {
        toast.error("No cashflow data available to export.");
        return;
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(buildPortfolioCashflowSheetData(rows, totals)),
        "Consolidated Cashflow"
      );
      XLSX.writeFile(wb, "consolidated-cashflow-all.xlsx");
    } catch (error) {
      console.error("Failed to export consolidated cashflow:", error);
      toast.error("Could not export cashflow data. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const canExport = !tableContent && !exporting;

  return (
    <div
      className={cn(
        "space-y-4",
        expanded && "fixed top-0 right-0 bottom-0 z-50 bg-white overflow-auto p-4 flex flex-col transition-[left,width] duration-300 ease-in-out"
      )}
      style={
        expanded
          ? { left: sidebarWidth, width: `calc(100% - ${sidebarWidth}px)` }
          : undefined
      }
    >
      <ConsolidatedCashflowKpiStrip
        className={cn(expanded && "hidden")}
        filters={kpiFilters}
      />

      {/* Filters */}
      <Card className="p-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">View:</span>
            <ToggleGroup
              type="single"
              size="sm"
              value={filters.granularity}
              onValueChange={(v) =>
                v && setFilters({ ...filters, granularity: v as "monthly" | "quarterly" })
              }
            >
              <ToggleGroupItem value="monthly" className="h-8 px-3 text-xs">
                Monthly
              </ToggleGroupItem>
              <ToggleGroupItem value="quarterly" className="h-8 px-3 text-xs">
                Quarterly
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="h-6 w-px bg-border" />

          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">FY:</span>
            <ToggleGroup
              type="single"
              size="sm"
              value={filters.fy}
              onValueChange={(v) => v && setFilters({ ...filters, fy: v })}
            >
              {fyOptions.map((fy) => (
                <ToggleGroupItem key={fy} value={fy} className="h-8 px-2.5 text-xs">
                  {fy.replace(/^FY/, "")}
                </ToggleGroupItem>
              ))}
              <ToggleGroupItem value="all" className="h-8 px-2.5 text-xs">
                All
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          <div className="h-6 w-px bg-border" />

          <MultiPick
            label="Customer"
            options={customerOptions}
            selected={filters.customerIds}
            onChange={(v) => setFilters({ ...filters, customerIds: v })}
          />

          <div className="ml-auto flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {tableRowCount} {filters.granularity === "monthly" ? "Months" : "Quarters"}
            </Badge>
            <CashflowGlossarySheet />
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleExport()}
              className="gap-1.5 h-8"
              disabled={!canExport}
            >
              {exporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => setExpanded((e) => !e)} className="gap-1.5 h-8">
              {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              {expanded ? "Collapse" : "Expand"}
            </Button>

          </div>
        </div>
      </Card>

      <Card className={cn("overflow-hidden min-w-0", expanded && "flex-1 min-h-0")}>
        <div className={cn("min-w-0", expanded ? "h-full min-h-[65vh]" : "h-[65vh]")}>
          {resolvedTableContent}
        </div>
      </Card>
    </div>
  );
};

export default ConsolidatedCashflowTab;
